import { Alert, Divider, Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import CueInput from './CueInput';
import { useDispatch, useSelector } from 'react-redux';
import { CueNoteItemsList } from './CueNotesList';
import { taskDetailsActions } from '../store/Tasks.redux';
import { IPlacementDetails } from 'app/models/Placement/PlacementDetails';
import { selectPlacementDetails } from 'store/redux-store/placement-details/selectors';
import { CuedTo, ICueListForPlacement, RepliedTo, TaskEntity } from 'app/models/Tasks/Tasks';
import { CueButtonGroup } from './CueButtonGroup';
import FilterIconClosed from 'app/assets/images/Tasks/activity-feed-filter-inactive.svg';
import { CueNotesFilter } from '../CustomComponents/CueNotesFilter';
import { selectUser } from 'oidc/user.selectors';
import FilterIconOpen from 'app/assets/images/Tasks/filter-selected.svg';
import { selectCueNotesList } from '../store/Tasks.selectors';
import { getCueNoteRecipientPayload } from 'store/redux-store/placement-details/helper';
import { getCueNotesCountByPlacementId } from 'app/services/TaskServices/TaskServices';
import { usePromiseTracker } from 'react-promise-tracker';
import { useTranslation } from 'react-i18next';
import { getEmployeeId } from '@AMIEWEB/Notification/Tasks/Common_v2/utils';

const CueNotesContainerStyles = makeStyles()(theme => ({
  messageBar: {
    bottom: 0,
    position: 'absolute',
    left: '0px',
    right: '0px',
    zIndex: 99,
  },
  filterIcon: {
    marginLeft: 'auto',
    '&:hover, &:focus': {
      backgroundColor: '#52429a29',
      boarderRadius: '4px',
    },
  },
  scroll: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column-reverse',
    padding: '5px 5px 0px 5px',
    overflowY: 'auto',
    overflowX: 'hidden',
  },
  buttonGroup: {
    display: 'flex',
    padding: '10px 12px',
    align: 'right',
  },
  filterIconActive: {
    marginLeft: 'auto',
    backgroundColor: '#52429a29',
    boarderRadius: '4px',
  },
  alert: { height: '46px', padding: '5px 5px' },
  alertGrid: { height: '15px', zIndex: 101, position: 'relative' },
}));

export const CueNotesContainer = ({
  clearCueField,
  container,
  setChangePanel,
}: {
  clearCueField: boolean;
  container: TaskEntity;
  setChangePanel: (e) => void;
}) => {
  const { t } = useTranslation();
  const { classes } = CueNotesContainerStyles();
  const dispatch = useDispatch();
  const { promiseInProgress: callingGetCueNotes } = usePromiseTracker({ area: 'get-cue-notes-alert', delay: 0 });

  const foundPlacement: IPlacementDetails = useSelector(selectPlacementDetails);
  const cueNotesList = useSelector(selectCueNotesList);
  const userInfo = useSelector(selectUser);

  const { placementId: urlPlacementId } = useParams<{ placementId: string }>();
  const currentPlacementId = Number(urlPlacementId || foundPlacement?.header?.placementId || 0);

  const [showRecipient, setShowRecipient] = useState<boolean>(false);
  const [offset, setOffset] = useState<number>();
  const [maxHeight, setMaxHeight] = useState<number>(9);
  const [showMyCues, setShowMyCues] = useState<boolean>(false);
  const [openFeedFilter, setFeedFilter] = useState(false);
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [dateRangeDisplay, setDateRangeDisplay] = useState<any | undefined | null>(null);

  const [cueCount, setCueCount] = useState<number | null>(0);
  const [lastCueNoteId, setLastCueNoteId] = useState<number | null>(null);
  const [selectedFilters, setSelectedFilters] = useState<any[]>([]);
  const [startDate, setStartDate] = useState<any>(null);
  const [endDate, setEndDate] = useState<any>(null);
  const [existingFilter, setExistingFilters] = useState<any[]>([]);

  const fetchCueCount = async (placementId: number) => {
    const response = await getCueNotesCountByPlacementId(placementId);
    if (response && response?.data) {
      setCueCount(response?.data);
    }
  };

  const getCueNotes = (alertUser = false) => {
    if (foundPlacement?.header?.placementId > 0) {
      const payloadData: ICueListForPlacement = {
        placementId: foundPlacement?.header?.placementId,
        currentUserId: showMyCues ? parseInt(getEmployeeId(userInfo?.userInfo)) : 0,
        alertUser: alertUser,
      };

      if (isFilterApplied) {
        if ((!selectedFilters || selectedFilters?.length === 0) && startDate === null && endDate === null) {
          setIsFilterApplied(false);
        } else {
          setIsFilterApplied(true);
        }
        setExistingFilters(selectedFilters);
        const data = {
          placementId: foundPlacement?.header?.placementId,
          createdStartDate: startDate || null,
          createdEndDate: endDate || null,
          cuedTo:
            selectedFilters[0]?.name === CuedTo ? selectedFilters[0]?.children?.map(c => parseInt(c.value)) : null,
          repliedToId:
            selectedFilters[0]?.name === RepliedTo ? selectedFilters[0]?.children?.map(c => parseInt(c.value)) : null,
        } as ICueListForPlacement;
        if (data?.placementId > 0) {
          dispatch(taskDetailsActions.getCueNotesForPlacements({ ...data, alertUser: alertUser }));
        }
      } else {
        if (showMyCues) {
          dispatch(taskDetailsActions.setCueListforPlacementOptions({ ...payloadData, alertUser: false }));
          dispatch(taskDetailsActions.getCueNotesForPlacements(payloadData));
        } else {
          dispatch(
            taskDetailsActions.setCueListforPlacementOptions({ placementId: foundPlacement?.header?.placementId }),
          );
          dispatch(
            taskDetailsActions.getCueNotesForPlacements({
              placementId: foundPlacement?.header?.placementId,
              alertUser: alertUser,
            }),
          );
        }
      }
      dispatch(taskDetailsActions.getCueNoteRecipient(getCueNoteRecipientPayload(foundPlacement?.header?.placementId)));
      !alertUser && fetchCueCount(foundPlacement?.header?.placementId);
    }
  };

  useEffect(() => {
    if (foundPlacement?.header?.placementId > 0 && foundPlacement?.header?.placementId === currentPlacementId) {
      getCueNotes();
      setFeedFilter(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMyCues, isFilterApplied, foundPlacement?.header?.placementId]);

  useEffect(() => {
    const pxToRemHeight = offset! / 16;
    setMaxHeight(pxToRemHeight);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [container, cueNotesList, showRecipient, offset]);

  return (
    <div style={{ width: 'inherit' }}>
      <Grid className={classes.buttonGroup}>
        <React.Fragment>
          <CueButtonGroup
            showMyCues={showMyCues}
            isFilterApplied={isFilterApplied}
            setIsFilterApplied={setIsFilterApplied}
            setShowMyCues={setShowMyCues}
            getCueNotes={getCueNotes}
            setLastCueNoteId={setLastCueNoteId}
          />
          {(cueNotesList?.length > 0 || isFilterApplied) && (
            <>
              {!isFilterApplied ? (
                <img src={FilterIconClosed} className={classes.filterIcon} onClick={() => setFeedFilter(true)} />
              ) : (
                <img src={FilterIconOpen} className={classes.filterIconActive} onClick={() => setFeedFilter(true)} />
              )}
            </>
          )}
          {/* <FilterIconClosed className={classes.filterIcon} onClick={() => setFeedFilter(true)} /> */}
          <div>
            <CueNotesFilter
              openFeedFilter={openFeedFilter}
              setFeedFilter={setFeedFilter}
              isFilterApplied={isFilterApplied}
              setIsFilterApplied={setIsFilterApplied}
              setShowMyCues={setShowMyCues}
              showMyCues={showMyCues}
              dateRangeDisplay={dateRangeDisplay}
              setDateRangeDisplay={setDateRangeDisplay}
              selectedFilters={selectedFilters}
              setSelectedFilters={setSelectedFilters}
              startDate={startDate}
              setStartDate={setStartDate}
              endDate={endDate}
              setEndDate={setEndDate}
              existingFilter={existingFilter}
              setExistingFilters={setExistingFilters}
            />
          </div>
        </React.Fragment>
      </Grid>
      <Grid className={classes.alertGrid}>
        {callingGetCueNotes && (
          <Alert className={classes.alert} severity="warning">
            {t('notification.taskSidePanel.cueNotes.changesFound')}
          </Alert>
        )}
      </Grid>
      <Grid className={classes.scroll} style={{ maxHeight: `calc(86vh - (${maxHeight}rem + 4rem))` }}>
        <CueNoteItemsList lastCueNoteId={lastCueNoteId} />
      </Grid>
      <Grid className={classes.messageBar}>
        <Divider />
        <CueInput
          setChangePanel={setChangePanel}
          setShowRecipient={setShowRecipient}
          clearCueField={clearCueField}
          setOffset={setOffset}
          getCueNotes={getCueNotes}
          cueCount={cueCount}
          setCueCount={setCueCount}
          lastCueNoteId={lastCueNoteId}
          setLastCueNoteId={setLastCueNoteId}
          selectedFilters={selectedFilters}
          startDate={startDate}
          endDate={endDate}
          isFilterApplied={isFilterApplied}
        />
      </Grid>
    </div>
  );
};
